
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Image, 
  Paperclip, 
  X, 
  Tag,
  Plus,
  Send,
  Shield,
  Link as LinkIcon
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePostsStore } from "@/store/postsStore";
import { Badge } from "@/components/ui/badge";
import { sanitizeInput } from "@/utils/security";

interface CreatePostFormProps {
  projectId?: string;
  onSuccess?: () => void;
}

export const CreatePostForm: React.FC<CreatePostFormProps> = ({ 
  projectId,
  onSuccess
}) => {
  const { addPost } = usePostsStore();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [newTag, setNewTag] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      // Sanitize input to prevent XSS
      const sanitizedTag = sanitizeInput(newTag);
      setTags([...tags, sanitizedTag]);
      setNewTag("");
    } else if (tags.includes(newTag.trim())) {
      toast({
        title: "Tag already added",
        description: "This tag is already in your list.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files || [])]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in both title and content.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    // Convert files to attachments
    const attachments = files.map(file => ({
      id: Math.random().toString(36).substring(2),
      name: file.name,
      type: file.type,
      url: URL.createObjectURL(file),
      size: file.size,
    }));

    // Create the post
    addPost({
      title,
      content,
      authorId: "user123", // In a real app, this would come from auth
      authorName: "Current User", // In a real app, this would come from auth
      tags,
      attachments,
      projectId,
    });

    // Reset form
    setTitle("");
    setContent("");
    setTags([]);
    setFiles([]);
    setIsSubmitting(false);

    toast({
      title: "Post created successfully!",
      description: "Your post has been published.",
    });

    if (onSuccess) {
      onSuccess();
    }
  };

  return (
    <Card className="border-blue-100 mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl flex items-center gap-2">
          Create a Post
          <Shield className="h-4 w-4 text-emerald-500" />
        </CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="Enter post title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border-blue-100"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              placeholder="Share your thoughts, updates, or questions..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[120px] border-blue-100"
            />
          </div>

          {projectId && (
            <div className="flex items-center gap-2 text-sm text-sky-600 bg-sky-50 p-2 rounded-md">
              <LinkIcon className="h-4 w-4" />
              <span>This post will be linked to your project</span>
            </div>
          )}

          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Add tags"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                className="border-blue-100"
              />
              <Button 
                type="button"
                onClick={handleAddTag}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Tag className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary"
                    className="flex items-center gap-1 bg-sky-100 text-sky-700"
                  >
                    {tag}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                      onClick={() => handleRemoveTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Attachments</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Add Files
              </Button>
              <input
                id="file-upload"
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((file, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                  >
                    <div className="flex items-center">
                      <Image className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-700">{file.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-gray-500 hover:text-red-500"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600"
            disabled={isSubmitting || !title.trim() || !content.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Publish Post
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};
